import React, { useState, useEffect, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import RouteFallback from './RouteFallback';
import { normalizeRole, needsPolicyAcceptance } from '../utils/roles';

const PolicyAcceptanceModal = React.lazy(() => import('./PolicyAcceptanceModal'));

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  allowedDepartments?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles, allowedDepartments }) => {
  const { isAuthenticated, isLoading, checkPolicies, user, hasAcceptedPolicies } = useAuth();
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return;
    if (!needsPolicyAcceptance(user.role)) {
      setShowPolicyModal(false);
      return;
    }
    setShowPolicyModal(!hasAcceptedPolicies);
  }, [isAuthenticated, isLoading, user, hasAcceptedPolicies]);

  if (isLoading) {
    return <div className="loading-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user && (allowedRoles || allowedDepartments)) {
    const roleOk = allowedRoles
      ? allowedRoles.map(normalizeRole).includes(normalizeRole(user.role))
      : false;
    const deptOk = allowedDepartments
      ? allowedDepartments.some((d) => d.toLowerCase() === user.department?.toLowerCase())
      : false;
    if (!roleOk && !deptOk) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  if (showPolicyModal && user && needsPolicyAcceptance(user.role)) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <PolicyAcceptanceModal
          onAccept={async () => {
            await checkPolicies();
            setShowPolicyModal(false);
          }}
        />
      </Suspense>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
